import {gql, type TypedDocumentNode} from '@apollo/client';
import {useQuery} from '@apollo/client/react';
import dayjs from 'dayjs';
import {useMemo} from 'react';

import {RussianDayOfTheWeek} from '../../i18n/RussianDayOfTheWeek';
import type {_Array, _Object} from '../../Paramut';
import createStart from '../../SupportGroups/createStart';
import getUpcomingEvent from '../../SupportGroups/getUpcomingEvent';
import type {ScheduleRecords} from '../../SupportGroups/ScheduleRecords';
import type {Millisecond} from '../../Time';
import getLocalTimeIfDiffers from '../../util/getLocalTimeIfDiffers';
import shiftTimeByTimeZone from '../../util/shiftTimeByTimeZone';
import type {
  GetGroupsByCityIdQuery,
  GetGroupsByCityIdQueryVariables,
} from './useCityGroups.generated';

export type UseCityGroupsProps = {
  now: Millisecond;
  cityId: number;
};

export type UseCityGroupsResult = {
  city?: City | null;
  groups?: Groups | null;
  localTime?: string;
};

export type City<Mut extends boolean = false> = _Object<
  {
    name: string;
    timezone: string;
    phoneNumber?: string;
    phoneNumberDescription?: string;
  },
  Mut
>;

export type Groups<Mut extends boolean = false> = _Array<Group<Mut>, Mut>;

export type Group<Mut extends boolean = false> = _Object<
  {
    id: number;
    name: string;
    address: string;
    location: {street: string; house: string};
    upcomingEvent: {day: string; start: string} | null;
    events: Meetings<Mut>;
  },
  Mut
>;

export type Meetings<Mut extends boolean = false> = _Array<Meeting<Mut>, Mut>;

export type Meeting<Mut extends boolean = false> = _Object<
  {
    id: number;
    /**
     * @example 0
     */
    sequence: number;
    /**
     * @example "2025-10-03T22:06:33.915Z"
     */
    updatedAt: string;
    /**
     * @example 6 // Saturday
     */
    day: number;
    start: _Object<
      {
        hour: number;
        minute: number;
      },
      Mut
    >;
    duration: number;
  },
  Mut
>;

export default function useCityGroups(
  props: UseCityGroupsProps,
): UseCityGroupsResult {
  const {now, cityId} = props;
  const {data} = useQuery(GET_GROUPS_BY_CITY_ID, {
    variables: {cityId, sameCityId: cityId},
    fetchPolicy: 'cache-and-network',
  });
  const city = data?.City;
  const {timezone} = city ?? {};
  const supportGroupDocs = data?.SupportGroups?.docs;
  const groups = useMemo(
    () =>
      supportGroupDocs?.map(_ => {
        const {day, hour, minute} = shiftTimeByTimeZone(
          now,
          timezone ?? dayjs.tz.guess(),
        );
        const upcomingEvent = getUpcomingEvent(
          day,
          hour,
          minute,
          (_.events?.docs as ScheduleRecords | undefined) ?? [],
        );
        return {
          id: _.id,
          name: _.name,
          address: shortAddress(_.location),
          location: _.location,
          upcomingEvent: upcomingEvent
            ? {
                day: RussianDayOfTheWeek[upcomingEvent.day],
                start: createStart(
                  upcomingEvent.start.hour,
                  upcomingEvent.start.minute,
                ),
              }
            : null,
          events:
            _.events?.docs.map(
              _ =>
                ({
                  ..._,
                  updatedAt: _.updatedAt as string,
                  start: _.start as {hour: number; minute: number},
                }) satisfies Meeting,
            ) ?? [],
        } satisfies Group;
      }),
    [now, supportGroupDocs, timezone],
  );

  const localTime = useMemo(
    () => getLocalTimeIfDiffers(now, timezone),
    [now, timezone],
  );

  return {city, groups, localTime};
}

const GET_GROUPS_BY_CITY_ID: TypedDocumentNode<
  GetGroupsByCityIdQuery,
  GetGroupsByCityIdQueryVariables
> = gql`
  query GetGroupsByCityId($cityId: Int!, $sameCityId: JSON!) {
    City(id: $cityId) {
      id
      name
      timezone
      phoneNumber
      phoneNumberDescription
    }
    SupportGroups(
      pagination: false
      where: {city: {equals: $sameCityId}}
      sort: "_supportGroups_supportGroups_order"
    ) {
      docs {
        id
        name
        location {
          id
          street
          house
          apartmentOrOffice
        }
        events(limit: 0) {
          docs {
            id
            sequence
            day
            start {
              hour
              minute
            }
            duration
            updatedAt
          }
        }
        _supportGroups_supportGroups_order
      }
    }
  }
`;

type Address = {
  street: string;
  house: string;
  apartmentOrOffice?: string | null;
};

function shortAddress(_: Address) {
  return [...compact([`${_.street} ${_.house}`, _.apartmentOrOffice])].join(
    ', ',
  );
}

function* compact<T>(
  source: Iterable<T | undefined | null>,
): IterableIterator<T> {
  for (const element of source) {
    if (element !== undefined && element !== null) {
      yield element;
    }
  }
}
